import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import companiaService, { Compania } from '../../services/companiaService';
import CompaniaArcaPanel from './CompaniaArcaPanel';
import SearchableSelect from '../../components/SearchableSelect';
import { COND_IVA_COMPANIA_OPCIONES, COND_IVA_COMPANIA_VALUES, normalizarCondIvaCompania } from '../../constants/condIvaCompania';
import { formatDateLocal } from '../../utils/formatters';

const formatFechaMaskInput = (value: string, prevValue: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  const prevDigits = prevValue.replace(/\D/g, '');
  const typingForward = digits.length > prevDigits.length;

  if (digits.length <= 2) {
    return typingForward && digits.length === 2 ? `${digits}/` : digits;
  }
  if (digits.length <= 4) {
    const base = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return typingForward && digits.length === 4 ? `${base}/` : base;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const toFechaMaskDisplay = (value?: string | null): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  return formatDateLocal(trimmed);
};

const parseFechaMask = (value: string): Date | null => {
  const match = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const defaultForm: Compania = {
  idCompania: '',
  nombre: '',
  domicilio: '',
  cp: '',
  localidad: '',
  provincia: '',
  pais: '',
  telefono1: '',
  telefono2: '',
  fax1: '',
  fax2: '',
  mail1: '',
  mail2: '',
  condIva: '',
  cuit: '',
  ingresosBrutos: '',
  inicioActividades: '',
  logo: ''
};

const MAX_LOGO_SIZE_MB = 2;
const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;

const CompaniaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { idCompania } = useParams();
  const [formData, setFormData] = useState<Compania>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFileName, setLogoFileName] = useState('');
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [logoPreviewSrc, setLogoPreviewSrc] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  const condIvaNorm = (formData.condIva ?? '').trim().toUpperCase();
  const condIvaOpciones = useMemo(() => {
    const base = [...COND_IVA_COMPANIA_OPCIONES];
    if (condIvaNorm && !COND_IVA_COMPANIA_VALUES.has(condIvaNorm)) {
      return [{ value: formData.condIva!.trim(), label: `${formData.condIva!.trim()} (actual)` }, ...base];
    }
    return base;
  }, [condIvaNorm, formData.condIva]);

  const loadCompania = useCallback(async () => {
    if (!idCompania || idCompania === 'nuevo') return;
    try {
      setLoading(true);
      const compania = await companiaService.getCompania(idCompania);
      setFormData({
        ...compania,
        condIva: normalizarCondIvaCompania(compania.condIva),
        inicioActividades: toFechaMaskDisplay(compania.inicioActividades),
      });
      setLogoPreviewSrc(compania.logo || '');
    } catch (error) {
      enqueueSnackbar('Error al cargar la compañía', { variant: 'error' });
      navigate('/sistema/companias');
    } finally {
      setLoading(false);
    }
  }, [idCompania, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (idCompania && idCompania !== 'nuevo') loadCompania();
  }, [idCompania, loadCompania]);

  useEffect(() => {
    setLogoPreviewError(false);
  }, [formData.logo, logoPreviewSrc]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'logo') {
      setLogoPreviewSrc(value);
      setLogoFileName('');
    }
  };

  const handleInicioActividadesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const prev = formData.inicioActividades || '';
    const masked = formatFechaMaskInput(e.target.value, prev);
    setFormData(prevForm => ({ ...prevForm, inicioActividades: masked }));
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Seleccione un archivo de imagen válido', { variant: 'warning' });
      e.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      enqueueSnackbar(`La imagen supera el tamaño máximo permitido de ${MAX_LOGO_SIZE_MB} MB.`, { variant: 'warning' });
      e.target.value = '';
      return;
    }

    const companyId = formData.idCompania.trim();
    if (!companyId) {
      enqueueSnackbar('Ingrese el ID de compañía antes de subir el logo.', { variant: 'warning' });
      e.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setLogoPreviewSrc(objectUrl);
    setLogoPreviewError(false);
    setLogoFileName(file.name);

    try {
      setUploadingLogo(true);
      const result = await companiaService.uploadCompaniaLogo(companyId, file);
      setFormData(prev => ({ ...prev, idCompania: companyId, logo: result.url }));
      setLogoPreviewSrc(result.url);
      enqueueSnackbar('Logo subido correctamente', { variant: 'success' });
    } catch (error: any) {
      setLogoPreviewSrc(formData.logo || '');
      enqueueSnackbar(error?.response?.data?.message || 'No se pudo subir el logo', { variant: 'error' });
    } finally {
      setUploadingLogo(false);
      URL.revokeObjectURL(objectUrl);
      e.target.value = '';
    }
  };

  const clearLogo = () => {
    setFormData(prev => ({ ...prev, logo: '' }));
    setLogoFileName('');
    setLogoPreviewError(false);
    setLogoPreviewSrc('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.idCompania.trim() || !formData.nombre.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      const inicioActividades = (formData.inicioActividades || '').trim();
      if (inicioActividades) {
        const fecha = parseFechaMask(inicioActividades);
        if (!fecha) {
          enqueueSnackbar('Inicio de Actividades: ingrese una fecha válida (DD/MM/AAAA)', { variant: 'warning' });
          return;
        }
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        fecha.setHours(0, 0, 0, 0);
        if (fecha > hoy) {
          enqueueSnackbar('Inicio de Actividades no puede ser posterior a la fecha actual', { variant: 'warning' });
          return;
        }
      }

      if (idCompania && idCompania !== 'nuevo') {
        const normalizedIdCompania = formData.idCompania.trim();
        const payload: Compania = {
          ...formData,
          idCompania: normalizedIdCompania,
          condIva: normalizarCondIvaCompania(formData.condIva),
        };
        await companiaService.updateCompania(normalizedIdCompania, payload);
      } else {
        await companiaService.createCompania({
          ...formData,
          condIva: normalizarCondIvaCompania(formData.condIva),
        });
      }

      enqueueSnackbar('Compañía guardada exitosamente', { variant: 'success' });
      navigate('/sistema/companias');
    } catch (error: any) {
      console.error('Error al guardar compañía:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la compañía', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/companias')}
              sx={{ textTransform: 'none' }}
            >
              Compañías
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {idCompania && idCompania !== 'nuevo' ? 'Editar Compañía' : 'Nueva Compañía'}
            </Typography>
          </Stack>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={(_, v) => setCurrentTab(v)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.95rem', '&.Mui-selected': { color: '#1976d2', fontWeight: 600 } },
                '& .MuiTabs-indicator': { backgroundColor: '#1976d2' },
              }}
            >
              <Tab label="General" />
              {idCompania && idCompania !== 'nuevo' && <Tab label="Servicios web ARCA" />}
            </Tabs>
          </Box>

          {currentTab === 1 && idCompania && idCompania !== 'nuevo' ? (
            <CompaniaArcaPanel idCompania={formData.idCompania} cuitCompania={formData.cuit} />
          ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                  <TextField 
                    name="idCompania" 
                    label="ID Compañía" 
                    value={formData.idCompania} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(idCompania && idCompania !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField 
                  name="nombre" 
                  label="Nombre" 
                  value={formData.nombre} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="cuit" 
                  label="CUIT" 
                  value={formData.cuit || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchableSelect
                  name="condIva"
                  label="Condición IVA"
                  allowEmpty
                  emptyOptionLabel="Seleccione..."
                  value={formData.condIva?.trim() || null}
                  onChange={(v) => setFormData((prev) => ({ ...prev, condIva: v ?? '' }))}
                  options={condIvaOpciones}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="ingresosBrutos"
                  label="Nro. Ingresos Brutos"
                  value={formData.ingresosBrutos || ''}
                  onChange={handleChange}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="inicioActividades"
                  label="Inicio de Actividades"
                  value={formData.inicioActividades || ''}
                  onChange={handleInicioActividadesChange}
                  fullWidth
                  placeholder="DD/MM/AAAA"
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  name="domicilio" 
                  label="Domicilio" 
                  value={formData.domicilio || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="localidad" 
                  label="Localidad" 
                  value={formData.localidad || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="provincia" 
                  label="Provincia" 
                  value={formData.provincia || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="cp" 
                  label="Código Postal" 
                  value={formData.cp || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="telefono1" 
                  label="Teléfono 1" 
                  value={formData.telefono1 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="telefono2" 
                  label="Teléfono 2" 
                  value={formData.telefono2 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="mail1" 
                  label="Email 1" 
                  type="email"
                  value={formData.mail1 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="mail2" 
                  label="Email 2" 
                  type="email"
                  value={formData.mail2 || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="logo"
                  label="Logo (URL)"
                  value={formData.logo || ''}
                  onChange={handleChange}
                  fullWidth
                  placeholder="https://mi-dominio.com/logo.png"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Button variant="outlined" component="label" disabled={uploadingLogo} sx={{ borderRadius: 2, textTransform: 'none' }}>
                    {uploadingLogo ? 'Subiendo logo...' : 'Subir imagen de logo'}
                    <input type="file" accept="image/*" hidden onChange={handleLogoFileChange} />
                  </Button>
                  {logoFileName && (
                    <Typography variant="body2" color="text.secondary">
                      Archivo cargado: {logoFileName}
                    </Typography>
                  )}
                  {!!(formData.logo || logoPreviewSrc) && (
                    <Button variant="text" color="error" onClick={clearLogo} sx={{ textTransform: 'none' }}>
                      Quitar logo
                    </Button>
                  )}
                </Stack>
              </Grid>
              {!!(logoPreviewSrc || formData.logo) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#fafafa'
                    }}
                  >
                    {!logoPreviewError ? (
                      <Box
                        component="img"
                        src={logoPreviewSrc || formData.logo}
                        alt="Vista previa del logo"
                        referrerPolicy="no-referrer"
                        onError={() => setLogoPreviewError(true)}
                        onLoad={() => setLogoPreviewError(false)}
                        sx={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Stack spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          No se pudo cargar la vista previa del logo.
                        </Typography>
                        {(logoPreviewSrc || formData.logo)?.startsWith('http') && (
                          <Button
                            component="a"
                            href={logoPreviewSrc || formData.logo}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="text"
                            sx={{ textTransform: 'none' }}
                          >
                            Abrir imagen en una pestaña nueva
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/companias')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompaniaForm;
